import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MailCheck, KeyRound } from 'lucide-react';

const errText = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || '').filter(Boolean).join(' ') || fallback;
  return fallback;
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [email, setEmail] = useState('');
  const [requested, setRequested] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      setRequested(true);
      toast.success('Reset link sent if the account exists');
    } catch (error) {
      toast.error(errText(error, 'Could not process request'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: newPassword });
      toast.success('Password reset successfully. Please sign in.');
      navigate('/login');
    } catch (error) {
      toast.error(errText(error, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-blue-900 flex items-center justify-center p-6">
      <Card data-testid="reset-password-card" className="w-full max-w-md p-8">
        {token ? (
          <>
            <div className="text-center mb-8">
              <KeyRound className="w-8 h-8 mx-auto mb-3 text-accent" />
              <h1 className="text-3xl font-bold tracking-tight mb-2">Set a new password</h1>
              <p className="text-muted-foreground">Choose a strong password for your account</p>
            </div>
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  data-testid="reset-new-password-input"
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  data-testid="reset-confirm-password-input"
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="reset-submit-btn"
                type="submit"
                className="w-full"
                disabled={loading}
                style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
              </Button>
            </form>
          </>
        ) : requested ? (
          <div className="text-center space-y-4" data-testid="reset-requested-message">
            <MailCheck className="w-10 h-10 mx-auto text-accent" />
            <h1 className="text-2xl font-bold tracking-tight">Check your inbox</h1>
            <p className="text-muted-foreground">
              If an account exists for <span className="font-medium">{email}</span>, we've sent a
              password reset link. It expires in 60 minutes.
            </p>
            <p className="text-sm text-muted-foreground">
              Didn't get it? If email delivery is unavailable, sign in with your existing password and
              change it from your Profile page, or contact your administrator.
            </p>
            <Link to="/login" className="text-accent font-medium hover:underline text-sm">Back to sign in</Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold tracking-tight mb-2">Forgot password</h1>
              <p className="text-muted-foreground">Enter your email to receive a reset link</p>
            </div>
            <form onSubmit={handleRequest} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  data-testid="forgot-email-input"
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="forgot-submit-btn"
                type="submit"
                className="w-full"
                disabled={loading}
                style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Remembered it?{' '}
              <Link to="/login" className="text-accent font-medium hover:underline">Sign in</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
